import styled from '@emotion/styled';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getOngoingSectionDetails } from '@selectors/quizTaking';
import { useSelectorWithShallowEqual } from '@hooks/reduxHooks';
import { NTA_COLORS } from '../../constants';
import { ntaPageCss } from '../../typography';
import useNtaTimer from '../../Taking/useNtaTimer';

const AVATAR_ICON = '/nta-experience/header-avatar.svg';

const AvatarBox = styled.div`
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  border: 1px solid ${NTA_COLORS.avatarBorder};
  background: ${NTA_COLORS.white};
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const AvatarImage = styled.img`
  display: block;
  width: ${({ $photo }) => ($photo ? '100%' : '27px')};
  height: ${({ $photo }) => ($photo ? '100%' : '32px')};
  object-fit: ${({ $photo }) => ($photo ? 'cover' : 'none')};
`;

const InfoBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 191px;
  width: max-content;
  max-width: 100%;
`;

const InfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

const InfoLabel = styled.span`
  width: 98px;
  flex-shrink: 0;
  color: ${({ $muted }) =>
    $muted ? NTA_COLORS.textMuted : NTA_COLORS.textPrimary};
  font-size: 12px;
  font-weight: 400;
  line-height: ${({ $muted }) => ($muted ? '20px' : '16px')};
  white-space: nowrap;
  ${ntaPageCss}
`;

const Colon = styled.span`
  flex-shrink: 0;
  color: ${NTA_COLORS.textPrimary};
  font-size: 12px;
  font-weight: 700;
  line-height: 16px;
  ${ntaPageCss}
`;

const InfoValue = styled.span`
  color: ${NTA_COLORS.accentBlue};
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  white-space: nowrap;
  ${ntaPageCss}
`;

const TimerPill = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 20px;
  padding: 2px 6px;
  border-radius: 100px;
  background: ${NTA_COLORS.timerPill};
  color: ${NTA_COLORS.white};
  font-size: 12px;
  font-weight: 700;
  line-height: 12px;
  letter-spacing: 0;
  text-transform: uppercase;
`;

const NtaRemainingTimer = ({ enabled, onExpire }) => {
  const { remainingTime } = useNtaTimer(enabled, onExpire);

  if (!remainingTime) return null;

  return (
    <InfoRow>
      <InfoLabel $muted>Remaining Time</InfoLabel>
      <Colon>:</Colon>
      <TimerPill>{remainingTime}</TimerPill>
    </InfoRow>
  );
};

const NtaHeaderUserInfo = ({ showTimer = true, onTimerExpire }) => {
  const { myInfo } = useMyInfo();
  const ongoingSectionDetails = useSelectorWithShallowEqual(
    getOngoingSectionDetails
  );
  const timerKey = ongoingSectionDetails?.section?.uid ?? 'default';

  const candidateName = myInfo?.name;
  const candidateAvatar = myInfo?.thumbnail;

  return (
    <>
      <AvatarBox>
        <AvatarImage
          src={candidateAvatar || AVATAR_ICON}
          alt={candidateName || ''}
          $photo={!!candidateAvatar}
        />
      </AvatarBox>
      <InfoBlock>
        <InfoRow>
          <InfoLabel>Candidate Name</InfoLabel>
          <Colon>:</Colon>
          <InfoValue>{candidateName}</InfoValue>
        </InfoRow>
        {showTimer ? (
          <NtaRemainingTimer
            key={timerKey}
            enabled={showTimer}
            onExpire={onTimerExpire}
          />
        ) : null}
      </InfoBlock>
    </>
  );
};

export default NtaHeaderUserInfo;
